import React, { useEffect } from "react";
import { ClipboardList, Search, Calendar, X } from "lucide-react";
import { CustomHeader } from "../components/AuthHeader";
import { PastOrderRow } from "../components/PastOrderRow";
import { OrderDetailsModal } from "../components/OrderDetailsModal";
import { useOrderStore } from "../features/orders/orderStore";
import { Order } from "../features/orders/types";

export default function OrderHistoryScreen() {
  const { isLoading, error, fetchCustomerOrders, searchOrders } =
    useOrderStore();
  const [date, setDate] = React.useState("");
  const [searchText, setSearchText] = React.useState("");
  const [selectedOrder, setSelectedOrder] = React.useState<Order | null>(null);
  const [showDatePicker, setShowDatePicker] = React.useState(false);

  useEffect(() => {
    fetchCustomerOrders("delivered");
  }, [fetchCustomerOrders]);

  const filteredOrders = searchOrders(searchText, date);
  const hasActiveFilter = searchText.length > 0 || date.length > 0;

  return (
    <div className="bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white px-6 pt-5 pb-5 shadow-sm rounded-b-3xl mb-5">
        <CustomHeader
          title="Order History"
          subtitle="Review your previous laundry orders"
        />

        {/* Search + Date inside header */}
        <div className="mt-4">
          {!showDatePicker ? (
            <div className="flex items-center gap-2">
              <div className="flex items-center flex-1 bg-gray-50 border border-gray-100 rounded-2xl px-4 py-3 gap-2 ml-3">
                <Search className="w-4 h-4 text-[#44B6CE] flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Search by order ID..."
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    setDate("");
                  }}
                  className="flex-1 bg-transparent outline-none text-gray-700 text-sm placeholder-gray-400"
                />
                {searchText && (
                  <button onClick={() => setSearchText("")}>
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowDatePicker(true);
                  setSearchText("");
                }}
                className={`rounded-2xl p-3.5 flex items-center justify-center border transition-all mr-3 ${
                  date
                    ? "bg-[#44B6CE] border-[#44B6CE] text-white"
                    : "bg-gray-50 border-gray-100 text-gray-600"
                }`}
              >
                <Calendar className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="date"
                className="flex-1 bg-gray-50 border border-gray-100 rounded-2xl px-4 py-3 text-gray-700 text-sm outline-none focus:border-[#44B6CE] transition-colors"
                autoFocus
                onChange={(e) => setDate(e.target.value)}
                value={date}
              />
              <button
                type="button"
                onClick={() => {
                  setShowDatePicker(false);
                  setDate("");
                }}
                className="bg-gray-50 border border-gray-100 rounded-2xl p-3.5 flex items-center justify-center"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="px-5">
        {/* Active filter indicator */}
        {hasActiveFilter && (
          <div className="flex items-center justify-between mb-3 bg-[#44B6CE]/10 border border-[#44B6CE]/20 rounded-2xl px-4 py-2.5">
            <p className="text-xs font-semibold text-[#44B6CE]">
              {date ? `Filtered by: ${date}` : `Searching: "${searchText}"`}
            </p>
            <button
              onClick={() => {
                setSearchText("");
                setDate("");
                setShowDatePicker(false);
              }}
              className="text-xs font-semibold text-[#44B6CE] underline"
            >
              Clear
            </button>
          </div>
        )}

        {/* Order count label */}
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Order List
          </p>
          {!isLoading && filteredOrders.length > 0 && (
            <span className="text-xs font-semibold text-[#44B6CE] bg-[#44B6CE]/10 px-2.5 py-1 rounded-full">
              {filteredOrders.length}{" "}
              {filteredOrders.length === 1 ? "order" : "orders"}
            </span>
          )}
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <p className="text-sm text-gray-400">Loading your orders...</p>
          </div>
        )}

        {/* Error */}
        {!isLoading && error && (
          <div className="bg-red-50 border border-red-100 rounded-2xl px-5 py-4 flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="text-red-500"
              >
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-red-600">
                Something went wrong
              </p>
              <p className="text-xs text-red-400 mt-0.5">{error}</p>
            </div>
          </div>
        )}

        {/* Empty */}
        {!isLoading && !error && filteredOrders.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="bg-[#44B6CE]/10 rounded-full p-6">
              <ClipboardList className="h-9 w-9 text-[#44B6CE]" />
            </div>
            <div className="text-center">
              <p className="text-base font-bold text-gray-800">
                {hasActiveFilter ? "No matching orders" : "No orders yet"}
              </p>
              <p className="text-sm text-gray-400 mt-1 max-w-[220px] mx-auto">
                {hasActiveFilter
                  ? "Try adjusting your search or date filter."
                  : "Your completed orders will appear here once delivered."}
              </p>
              {hasActiveFilter && (
                <button
                  onClick={() => {
                    setSearchText("");
                    setDate("");
                    setShowDatePicker(false);
                  }}
                  className="mt-3 text-sm font-semibold text-[#44B6CE]"
                >
                  Clear filter
                </button>
              )}
            </div>
          </div>
        )}

        {/* Orders list */}
        {!isLoading && !error && filteredOrders.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden divide-y divide-gray-100">
            {filteredOrders.map((order, idx) => (
              <PastOrderRow
                key={idx}
                orderId={order.order_no}
                date={order.delivery_date}
                status="Delivered"
                onClick={() => setSelectedOrder(order)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      <OrderDetailsModal
        open={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
        order={selectedOrder}
        onDownloadInvoice={() => {}}
      />
    </div>
  );
}
